import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../assets/css/common.css';
import '../assets/css/member-list.css';
import CustomButtons from '../components/CustomButtons';
import { formatYen } from '../utils/format';
import { adminService } from '../services/adminService';
import { buildCsv, downloadCsv } from '../utils/csv';
import { toWareki } from '../utils/wareki';

const PAGE_SIZE = 20;

function MemberList() {
  const [members, setMembers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [roleId, setRoleId] = useState(null);
  const [branches, setBranches] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 検索/フィルタ/ソート状態
  const [keyword, setKeyword] = useState('');
  const [filterBranchId, setFilterBranchId] = useState(searchParams.get('branchId') || '');
  const [filterRoleId, setFilterRoleId] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('ASC');

  // リセット後のfetch用フラグ
  const resetFlag = useRef(false);

  // CSVダウンロード中フラグ
  const [exporting, setExporting] = useState(false);

  const roles = [
    { id: 1, name: 'root' },
    { id: 2, name: '管理者' },
    { id: 3, name: '人事' },
    { id: 4, name: '支社長' },
    { id: 5, name: '一般社員' }
  ];

  const fetchMembers = useCallback(async (p, overrides = {}) => {
    try {
      const data = await adminService.getMembers({
        page: p,
        limit: PAGE_SIZE,
        keyword: overrides.keyword !== undefined ? overrides.keyword : (keyword || undefined),
        branchId: overrides.branchId !== undefined ? overrides.branchId : (filterBranchId || undefined),
        roleId: overrides.roleId !== undefined ? overrides.roleId : (filterRoleId || undefined),
        sortBy: overrides.sortBy || sortBy,
        sortOrder: overrides.sortOrder || sortOrder
      });
      setMembers(data.members || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
      setRoleId(data.roleId ?? null);
    } catch (err) {
      console.error(err);
    }
  }, [keyword, filterBranchId, filterRoleId, sortBy, sortOrder]);

  useEffect(() => {
    if (resetFlag.current) {
      resetFlag.current = false;
      return; // リセット時は下のresetEffect側でfetchする
    }
    fetchMembers(page);
  }, [page, fetchMembers]);

  useEffect(() => {
    adminService.getBranches()
      .then(data => setBranches(data.branches || []))
      .catch(() => {});
  }, []);

  const handleSearch = () => {
    setPage(1);
    fetchMembers(1);
  };

  const handleReset = () => {
    setKeyword('');
    setFilterBranchId('');
    setFilterRoleId('');
    setSortBy('created_at');
    setSortOrder('ASC');
    setPage(1);
    // リセット後のfetchはデフォルト条件で即実行
    fetchMembers(1, { keyword: undefined, branchId: undefined, roleId: undefined, sortBy: 'created_at', sortOrder: 'ASC' });
    resetFlag.current = true;
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
    setPage(1);
  };

  const sortIndicator = (column) => {
    if (sortBy !== column) return '';
    return sortOrder === 'ASC' ? ' ▲' : ' ▼';
  };

  const handleDetail = (id, type) => {
    navigate(`/member-detail/${id}/${type}`);
  };

  const permissionMap = { 1: '承諾する', 2: '承諾しない' };

  // 現在の検索条件でCSVダウンロード
  const handleCsvDownload = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const data = await adminService.exportMembers({
        keyword: keyword || undefined,
        branchId: filterBranchId || undefined,
        roleId: filterRoleId || undefined,
        sortBy,
        sortOrder
      });

      const headers = [
        '氏名', 'フリガナ', 'メールアドレス', '拠点', '権限',
        'メンバー登録', '生年月日', '生年月日（和暦）',
        '郵便番号（住民票）', '住所（住民票）', '郵便番号（郵送先）', '住所（郵送先）',
        '最寄駅', '電話番号', '血液型',
        '銀行名', '支店名', '口座種別', '口座番号',
        '扶養家族の有無', '扶養家族数', '定期代合計',
        '画像使用許諾', '秘密保持誓約', '登録日'
      ];
      const rows = (data.members || []).map(m => [
        m.name, m.furigana, m.email, m.branchName, m.roleName,
        m.hasMemberData ? '登録済み' : '未登録',
        m.birthDate, toWareki(m.birthDate),
        m.postalCode, m.residenceAddress, m.mailingPostalCode, m.mailingAddress,
        m.nearestStation, m.phoneNumber, m.bloodType,
        m.bankName, m.bankBranchName, m.accountType, m.accountNumber,
        m.hasDependents === 1 ? '有' : m.hasDependents === 0 ? '無' : (m.dependentCount > 0 ? '有' : ''),
        m.dependentCount,
        m.routeCost !== null ? m.routeCost : '',
        m.photoPermission !== null ? (permissionMap[m.photoPermission] || m.photoPermission) : '未登録',
        m.hasConfidentialityData ? '同意済み' : '未同意',
        m.createdAt
      ]);

      const now = new Date();
      const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      downloadCsv(`メンバー一覧_${stamp}.csv`, buildCsv(headers, rows));
    } catch (err) {
      console.error('CSVダウンロードエラー:', err);
      alert('CSVダウンロードに失敗しました。');
    } finally {
      setExporting(false);
    }
  };

  // 件数表示テキスト
  const countText = total === 0
    ? '該当するメンバーがいません'
    : `全${total}件中 ${(page - 1) * PAGE_SIZE + 1}〜${Math.min(page * PAGE_SIZE, total)}件表示`;

  return (
    <div className="container">
      <h1 className="text-primary text-center mb-4">
        {roleId === 1 || roleId === 2 || roleId === 3
          ? '全メンバー一覧'
          : roleId === 4
            ? '支社メンバー一覧'
            : 'メンバー一覧'}
      </h1>

      {/* 検索/フィルタ */}
      <div className="search-controls">
        <div className="search-row">
          <input
            type="text"
            className="search-input"
            placeholder="氏名・メール・フリガナで検索"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          {roleId !== 4 && (
            <select
              className="search-select"
              value={filterBranchId}
              onChange={(e) => setFilterBranchId(e.target.value)}
            >
              <option value="">全拠点</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}
          <select
            className="search-select"
            value={filterRoleId}
            onChange={(e) => setFilterRoleId(e.target.value)}
          >
            <option value="">全権限</option>
            {roles.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <button className="btn btn-primary btn-sm" onClick={handleSearch}>検索</button>
          <button className="btn btn-success btn-sm" onClick={handleCsvDownload} disabled={exporting}>
            {exporting ? 'ダウンロード中...' : 'CSVダウンロード'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={handleReset}>条件クリア</button>
        </div>
        <div className="search-meta">
          {countText}
        </div>
      </div>

      {/* ソートボタン */}
      <div className="sort-controls">
        <span className="sort-label">並び替え:</span>
        <button
          className={`btn btn-sm ${sortBy === 'created_at' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => handleSort('created_at')}
        >
          登録日{sortIndicator('created_at')}
        </button>
        <button
          className={`btn btn-sm ${sortBy === 'furigana' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => handleSort('furigana')}
        >
          フリガナ{sortIndicator('furigana')}
        </button>
      </div>

      <table className="details-table">
        <thead>
          <tr>
            <th>氏名</th>
            <th>拠点</th>
            <th>メンバー登録</th>
            <th>交通経路金額</th>
            <th>画像使用許諾</th>
            <th>秘密保持誓約状況</th>
          </tr>
        </thead>
        <tbody>
          {members.length === 0 ? (
            <tr><td colSpan="6" className="text-muted">該当するメンバーがいません</td></tr>
          ) : (
            members.map((m) => (
              <tr key={m.id}>
                <td>
                  {m.furigana && <div className="text-small text-muted">{m.furigana}</div>}
                  <div>
                    <button className="btn btn-link p-0 text-start" style={{textDecoration:'none',fontWeight:'bold'}} onClick={() => handleDetail(m.id, 'member')}>
                      {m.name}
                    </button>
                  </div>
                </td>
                <td>{m.branchName || '-'}</td>
                <td>
                  {m.hasMemberData ? (
                    <>
                      <div className="mt-1">登録済み</div>
                      <button className="btn btn-primary btn-sm" onClick={() => handleDetail(m.id, 'member')}>詳細</button>
                    </>
                  ) : (
                    <span className="text-muted">未登録</span>
                  )}
                </td>
                <td>
                  {m.routeCost !== null ? (
                    <>
                      <div className="mt-1">{formatYen(m.routeCost)}</div>
                      <button className="btn btn-primary btn-sm" onClick={() => handleDetail(m.id, 'route')}>詳細</button>
                    </>
                  ) : (
                    <span className="text-muted">未登録</span>
                  )}
                </td>
                <td>
                  {m.photoPermission !== null ? (
                    <>
                      <div className="mt-1">{permissionMap[parseInt(m.photoPermission)] || m.photoPermission}</div>
                      <button className="btn btn-primary btn-sm" onClick={() => handleDetail(m.id, 'photo')}>詳細</button>
                    </>
                  ) : (
                    <span className="text-muted">未登録</span>
                  )}
                </td>
                <td>
                  {m.hasConfidentialityData ? (
                    <div className="mt-1">同意済み</div>
                  ) : (
                    '未同意'
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="pagination-controls">
        <button className="btn btn-secondary" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>
          前へ
        </button>
        <span>{page} / {totalPages}</span>
        <button className="btn btn-secondary" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}>
          次へ
        </button>
      </div>
      <CustomButtons
        rightLabel="マイページへ戻る"
        onRightClick={() => navigate('/')}
        leftLabel=""
        leftClassName="d-none"
      />
    </div>
  );
}

export default MemberList;
